import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from './client';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  isConfigured: boolean;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const NOT_CONFIGURED_ERROR = new Error('Supabase is not configured.');

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const configured = isSupabaseConfigured();
  const client = supabase;

  // When Supabase isn't configured we never need to wait — flip loading off
  // immediately so the rest of the tree renders without a spinner.
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(configured);

  useEffect(() => {
    if (!client) return;

    // Cancellation guard: if the provider unmounts before getSession()
    // resolves, skip the state updates. The lint rule can't see the mutation
    // through the async closure, so we suppress it on the read.
    let cancelled = false;

    void (async () => {
      const { data } = await client.auth.getSession();
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (cancelled) return;
      setSession(data.session);
      setLoading(false);
    })();

    const { data: sub } = client.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      // onAuthStateChange fires synchronously after sign-in/out, so we also
      // clear loading here in case getSession() hasn't resolved yet.
      setLoading(false);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [client]);

  const signInWithMagicLink = async (
    email: string,
  ): Promise<{ error: Error | null }> => {
    if (!client) return { error: NOT_CONFIGURED_ERROR };
    const { error } = await client.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    return { error: error ?? null };
  };

  const signOut = async (): Promise<void> => {
    if (!client) return;
    await client.auth.signOut();
  };

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    loading,
    isConfigured: configured,
    signInWithMagicLink,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- co-located hook for the provider in this file
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
